import { useEffect, useContext } from "react";
import moment from "moment";
import { connect } from "react-redux";
import PhoneInput from "react-phone-number-input";
import { stateType } from "../../store";
import { BlackListWrapper } from "../../pages/CheckOut/CheckOut";
import CheckOutButtons from "../../pages/CheckOut/CheckOutButtons";
import { setOrderDetails } from "../../state_management/actions";
import {
  isComplete,
  orderType,
  proceed_val_type
} from "../../state_management/reducers/order";
import Input from "../../UI/Input/Input";
import { getCookie } from "../../utilities/cookieManager";
import CheckOutList from "../CheckOutList/CheckOutList";
import "react-phone-number-input/style.css";
import "./CheckOutAddress.css";
import {
  coupon_error_message,
  getCurrency,
  isEmail,
  isMobile
} from "../../utilities/misc";
import {
  setCheckOutError,
  setEmailBlacklisted,
  setPageCount
} from "../../state_management/actions/common";
import { useAsync } from "../../utilities/asyncFunc";
import { checkEmail } from "../../state_management/epics/order";
import {
  ga_params_type,
  add_shipping_info_ga
} from "../../utilities/analytics";
import { cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { Raw } from "../../App";
import { domain as d } from "../../settings";
import {
  epics_cart_items,
  only_gift,
  split_cart
} from "../../utilities/cartUtilities";
import {
  CouponObjectContext,
  LocaleContext,
  TotalWithCouponContext
} from "../../AppProvider";

const fullName = (firstname: string | null, surname: string | null): string => {
  firstname = firstname || ``;
  const hasSurname = typeof surname === "string";
  const fullName = firstname + (hasSurname ? ` ${surname}` : "");
  return fullName;
};

const splitNames = (input: string): string[] => {
  const trimmedInput = input.trim(); // Remove leading and trailing spaces
  const splitArray = trimmedInput.split(/\s+/); // Split by one or more spaces

  const trailingSpaces = " ".repeat(
    (() => {
      const match = input.match(/[^ ]\s*$/);
      if (match) {
        return match[0].length - 1; // Subtract 1 to exclude the non-space character
      }
      return 0;
    })()
  );

  // Add back leading and trailing spaces if present
  // splitArray[0] = leadingSpaces + splitArray[0];
  splitArray[splitArray.length - 1] += trailingSpaces;

  return splitArray;
};

const CheckOutAddress = ({
  orderDetails,
  setOrderDetails,
  checkoutError,
  emailBlacklisted,
  setEmailBlackListed,
  setPageCount,
  pageCount,
  setCheckOutError,
  cart,
  extras
}: {
  orderDetails: orderType;
  setOrderDetails: (details: orderType) => void;
  checkoutError: boolean;
  emailBlacklisted: boolean;
  setEmailBlackListed: (data: boolean) => void;
  setPageCount: (data: number) => void;
  pageCount: proceed_val_type;
  setCheckOutError: (data: boolean) => void;
  cart: cartItem[];
  extras: extrasCartItem[];
}) => {
  const { t, locale } = useContext(LocaleContext);
  const [totalWithCoupon] = useContext(TotalWithCouponContext);

  const getNameError = (): string => {
    let errorMsg = "";
    const firstname = orderDetails.client_firstname?.trim();
    const lastname = orderDetails.client_lastname?.trim();

    if (!firstname && lastname) {
      errorMsg = t("Please enter your firstname");
    } else if (!firstname && !lastname) {
      errorMsg = t("Please enter your firstname and your lastname");
    } else if (!lastname) {
      errorMsg = t("Please enter your lastname");
    }

    return errorMsg;
  };

  const { cart: cart_items, gift_values: gift } = split_cart(cart);
  const onlyGift = only_gift({ cart, extras });
  const proceed = isComplete(pageCount, orderDetails, false, onlyGift);

  const {
    CouponObject: { couponRes, couponErrorType, errorBanner }
  } = useContext(CouponObjectContext);

  const emailBlackListCheckRes = useAsync({
    asyncFunc: checkEmail,
    funcParams: {},
    immediate: false,
    clearOnError: true
  });

  const parseCart: ga_params_type["items"] = [
    ...cart.map((item) => ({
      id: item.id,
      type: item.type,
      quantity: item.quantity,
      item: item.item,
      extra: false
    })),
    ...extras.map((item) => ({
      id: item.id,
      type: item.type,
      item: item.item,
      extra: true,
      quantity: item.quantity
    }))
  ];

  const nextFunction = async () => {
    try {
      if (proceed) {
        const isString = (value: string | null) => typeof value === "string";
        const client_firstname = isString(orderDetails.client_firstname)
          ? orderDetails.client_firstname?.trim() || null
          : null;
        const client_lastname = isString(orderDetails.client_lastname)
          ? orderDetails.client_lastname?.trim() || null
          : null;
        setOrderDetails({ ...orderDetails, client_firstname, client_lastname });

        add_shipping_info_ga({
          coupon: orderDetails.coupon_code,
          currency: getCurrency(),
          items: parseCart,
          value: totalWithCoupon
        });

        if (orderDetails.coupon_code) {
          const res = await couponRes.execute({
            coupon_code: orderDetails.coupon_code,
            details: orderDetails,
            show_banner: true,
            ...epics_cart_items({ cart: cart_items, extras, gift_values: gift })
          });

          if (!res) return;
        }

        // make the request
        emailBlackListCheckRes.execute({
          email: orderDetails.client_email || undefined
        });
      } else {
        setCheckOutError(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const called = emailBlackListCheckRes.called;
    const success = !!emailBlackListCheckRes.data?.success;

    if (called && !emailBlackListCheckRes.error && success) {
      if (proceed) {
        // if successful, proceed to order page by increasing count.
        setPageCount(pageCount + 1);
      } else {
        setCheckOutError(true);
      }
    }
  }, [emailBlackListCheckRes.data]);

  useEffect(() => {
    const hasError = !!emailBlackListCheckRes.error;

    if (hasError) {
      // If usuccessful, show error
      setEmailBlackListed(true);
    } else {
      setEmailBlackListed(false);
    }
  }, [emailBlackListCheckRes.error]);

  useEffect(() => {
    return () => {
      setEmailBlackListed(false);
    };
  }, []);

  const getEmailError = (): string => {
    if (!orderDetails.client_email) {
      return t("Please enter your email");
    } else if (!isEmail(orderDetails.client_email)) {
      return t("Please enter a valid email");
    } else {
      return "";
    }
  };

  const getMobileError = (): string => {
    if (!orderDetails.client_mobile) {
      return t("Please enter your phone number");
    } else if (!isMobile(orderDetails.client_mobile)) {
      return t("Please enter a valid phone number");
    } else {
      return "";
    }
  };

  const loading = emailBlackListCheckRes.loading;
  const error = emailBlackListCheckRes.error;

  const defaultPhoneCountry = locale === "eng" ? "IE" : "LT";
  const domain = d(locale);

  return (
    <div className="checkOutAddress_wrapper">
      <BlackListWrapper
        listObjects={[
          {
            inView: emailBlacklisted,
            text: error ? error : t("Email is blacklisted"),
            watch: !!(!loading && error && emailBlacklisted)
          },
          {
            inView: errorBanner && (!!couponRes.error || !!couponErrorType),
            text: couponErrorType
              ? coupon_error_message(couponErrorType, t)
              : "Couldn't apply coupon",
            watch: !!(
              !couponRes.loading &&
              errorBanner &&
              (!!couponRes.error || !!couponErrorType)
            )
          }
        ]}
      />
      <div className="flex checkout_content_wrapper">
        <div className="checkout_left">
          <div>
            <p className="checkoutAddress_introLabel med_text">
              {t("Please provide your details in the form below to proceed.")}
            </p>
            <div className="checkout_content">
              <div>
                <Input
                  label={t("Name")}
                  error={
                    checkoutError &&
                    (!orderDetails.client_firstname?.trim() ||
                      !orderDetails.client_lastname?.trim())
                      ? getNameError()
                      : undefined
                  }
                  value={fullName(
                    orderDetails.client_firstname,
                    orderDetails.client_lastname
                  )}
                  handler={(name) => {
                    const nameArray = splitNames(name);
                    const count = nameArray.length;
                    const client_firstname = nameArray[0] || null;
                    const client_lastname =
                      count > 1 ? nameArray.slice(1).join(` `) : null;

                    setOrderDetails({
                      ...orderDetails,
                      client_firstname,
                      client_lastname
                    });
                  }}
                />
                <div className="checkout_input_grid">
                  <Input
                    label={t("Email")}
                    type="email"
                    error={
                      checkoutError &&
                      (!orderDetails.client_email ||
                        !isEmail(orderDetails.client_email))
                        ? getEmailError()
                        : undefined
                    }
                    value={orderDetails.client_email || ``}
                    handler={(client_email) => {
                      setOrderDetails({
                        ...orderDetails,
                        client_email
                      });
                    }}
                  />
                  <div className="input_wrapper ">
                    <label className="flex med_text">{t("Phone Number")}</label>
                    <PhoneInput
                      addInternationalOption={false}
                      placeholder=""
                      defaultCountry={defaultPhoneCountry}
                      value={orderDetails.client_mobile || undefined}
                      onChange={(mobile) => {
                        const client_mobile = mobile
                          ? (mobile as string)
                          : null;
                        setOrderDetails({
                          ...orderDetails,
                          client_mobile
                        });
                      }}
                    />
                    {checkoutError &&
                    (!orderDetails.client_mobile ||
                      !isMobile(orderDetails.client_mobile)) ? (
                      <span className="errorMsg">{getMobileError()}</span>
                    ) : (
                      ``
                    )}
                  </div>
                </div>
                <div className="checkout_pseudoAddress_wrapper">
                  <Input
                    label={t("Address")}
                    readOnly
                    value={getCookie(`cleanBee_checkout_address`)}
                    handler={(name) => {}}
                  />
                </div>
                <Raw>
                  {`
                    <p class="checkoutTerms">
                      ${t(
                        "By proceeding with your booking you agree to our {{Terms of service}} and {{Privacy Policy}}. We may occasionally send you newsletters with special offers. Don’t worry, you can easily unsubscribe at anytime.",
                        {
                          "{{Terms of service}}": `<span class="fontBlue"><a href="https://${domain}/terms-and-conditions/" target="_blank" rel="noreferrer">${t(
                            "Terms of service"
                          )}</a></span>`,
                          "{{Privacy Policy}}": ` <span class="fontBlue"><a href="https://${domain}/privacy-policy/" target="_blank" rel="noreferrer">${t(
                            "Privacy Policy"
                          )}</a></span>`
                        }
                      )}
                    </p>
                  `}
                </Raw>
                <div className="checkout_textArea_wrapper">
                  <span className="med_text">{t("Notes (optional)")}</span>
                  <textarea
                    onChange={(e) => {
                      const value = e.target.value;
                      setOrderDetails({
                        ...orderDetails,
                        info: value
                      });
                    }}
                    value={orderDetails.info || ``}
                    className="checkout_textArea"
                  ></textarea>
                </div>
              </div>
            </div>
            <CheckOutButtons
              nextFunction={nextFunction}
              loading={couponRes.loading || loading}
            />
          </div>
        </div>
        <div className="checkout_right">
          <CheckOutList
            nextFunction={nextFunction}
            loading={couponRes.loading || loading}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: stateType) => ({
  orderDetails: state.orderReducer.details,
  cart: state.cartReducer.cart,
  extras: state.cartReducer.extras,
  checkoutError: state.commonReloadReducer.checkoutError,
  emailBlacklisted: state.commonReloadReducer.emailBlacklisted,
  pageCount: state.commonReducer.pageCount
});

const mapDispatchToProps = (dispatch: any) => ({
  setOrderDetails: (details: orderType) => {
    dispatch(setOrderDetails(details));
  },
  setEmailBlackListed: (data: boolean) => {
    dispatch(setEmailBlacklisted(data));
  },
  setPageCount: (data: number) => {
    dispatch(setPageCount(data));
  },
  setCheckOutError: (data: boolean) => {
    dispatch(setCheckOutError(data));
  }
});

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(CheckOutAddress);

export default ConnectedComponent;

export const DateTimeInfoHeader = ({
  date,
  endTime,
  startTime
}: {
  date: string | undefined;
  startTime: string | undefined;
  endTime: string | undefined;
}) => {
  return date && startTime && endTime ? (
    <div className="checkoutAddress_introText">
      <div>Chosen cleaning time</div>
      <div className="checkoutAddress_introText_bottom">
        <div>
          <b>{moment(date).format("MMM DD, YYYY")}</b> from{" "}
          <b>
            {startTime || "-"} to {endTime || "-"}
          </b>
          .
        </div>
        <span>Free rescheduling up to 24 hours before your booking.</span>
      </div>
    </div>
  ) : null;
};
